import type { WireAuthLink, WireAuthProviderAdminRecord, WireAuthProviderListEntry } from '#layers/access/utils/auth-providers/wire';
import type { ResponseEnvelope } from '#layers/base/public';
import type { MonospaceOrgRole, MonospaceSystemWorkspaceMember, MonospaceUser, MonospaceUserOrgRole } from '#layers/data-model/public';
import { useEventListener } from '@vueuse/core';
import { defineStore } from 'pinia';
import { joinURL } from 'ufo';
import { computed, ref, shallowRef } from 'vue';
import * as z from 'zod/v4-mini';
import { useRuntimeConfig } from '#app/nuxt';
import { useLastLoginMethod } from '#layers/access/composables/use-last-login-method';
import { useSsoReturnTo } from '#layers/access/composables/use-sso-return-to';
import { AUTH_ROUTES, EMAIL_LOGIN_METHOD } from '#layers/access/constants/auth';
import { useOnboardingStore } from '#layers/access/stores/onboarding';
import { redirect } from '#layers/access/utils/redirect';
import { getRefreshDelayMs } from '#layers/access/utils/token-refresh';
import { createApiFetch, createRequestTracker, getErrorStatusCode, setUnauthorizedHandler, singleFlight, useSystemFetch, useSystemInfo } from '#layers/base/public';
import { useSystemEntityStore } from '#layers/runtime/public';
import { CURRENT_USER_FIELDS_QUERY } from './auth/current-user-fields';

const PROVIDERS_PATH = 'providers';

const LoginResult = z.object({
	expires: z.number(),
	requireAcceptTerms: z.optional(z.boolean()),
});

const RefreshAccessTokenResult = z.object({
	expires: z.number(),
});

type LoginResult = z.infer<typeof LoginResult>;
type RefreshAccessTokenResult = z.infer<typeof RefreshAccessTokenResult>;

export type AuthUser = MonospaceUser & {
	orgRole?: (Omit<MonospaceUserOrgRole, 'role'> & { role?: MonospaceOrgRole | null }) | null;
	workspaces?: MonospaceSystemWorkspaceMember[];
};

function createAsyncAction<TArgs extends unknown[], TResult>(action: (...args: TArgs) => Promise<TResult>) {
	const pending = ref(false);
	const error = shallowRef<unknown>(null);

	async function execute(...args: TArgs) {
		pending.value = true;

		try {
			const result = await action(...args);
			error.value = null;
			return result;
		}
		catch (error_) {
			error.value = error_;
			throw error_;
		}
		finally {
			pending.value = false;
		}
	}

	return { execute, pending, error };
}

export const useAuth = defineStore('auth', () => {
	const { refresh: refreshSystemInfo, resetAuthProbe } = useSystemInfo();
	const { overrideOnboardingResult } = useOnboardingStore();
	const ssoReturnTo = useSsoReturnTo();
	const lastLoginMethod = useLastLoginMethod();
	const loggedIn = ref(false);
	// `orgRole.role` is hydrated by `fetchCurrentUser`'s awaited `/me` fetch
	// (resolved before route gates run), so `isOrganizationAdmin` can read `isAdmin`.
	const currentUser = shallowRef<AuthUser | null>(null);

	// Mirrors the engine's `ensure_org_entitlements` check (an org role with `isAdmin`),
	// which gates org-level operations like workspace create/update/delete and org settings.
	// Resolves false while the user or role is still unloaded.
	const isOrganizationAdmin = computed(() => Boolean(currentUser.value?.orgRole?.role?.isAdmin));

	const apiEndpoint = useRuntimeConfig().public.api;
	const authApiEndpoint = joinURL(apiEndpoint, 'auth');
	// No unauthorized replay: recovery calls the refresh endpoint, so replaying a
	// failed refresh through recovery would recurse.
	const authFetch = createApiFetch({ baseURL: authApiEndpoint, retryUnauthorized: false });
	const refreshFetch = createApiFetch({ baseURL: authApiEndpoint, retryUnauthorized: false, reportUnauthorized: false });
	const systemFetch = useSystemFetch();

	let refreshDueAt = 0;
	let refreshAccessTokenTimeout: ReturnType<typeof setTimeout> | undefined;
	/**
	 * Set once logout starts. A refresh landing after it rotates the refresh token,
	 * and the engine invalidates a session by the jti it was handed — so the rotated
	 * session would survive the logout and sign the user straight back in.
	 */
	let isEndingSession = false;

	/**
	 * Session epoch. Requests that were already in flight when the session ended
	 * must not write their results back — `keepalive` and an unresolved promise
	 * both outlive `loggedOut()`, so clearing the timer is not enough.
	 */
	const sessionRequests = createRequestTracker();

	// The server rotates the refresh token, so a second concurrent refresh would
	// invalidate the first one's new cookie.
	const ensureFreshAccessToken = singleFlight(() => refreshAccessToken());

	function setAccessTokenExpiresIn(expiresIn: number) {
		const delay = getRefreshDelayMs(expiresIn);
		refreshDueAt = Date.now() + delay;

		clearTimeout(refreshAccessTokenTimeout);

		refreshAccessTokenTimeout = setTimeout(() => {
			void ensureFreshAccessToken();
		}, delay);
	}

	// Background tabs clamp timers, so a long idle returns with a refresh that fired
	// late or not at all. Catch up before the first action turns into a 401.
	function refreshOnReturnToTab() {
		if (isEndingSession || document.visibilityState !== 'visible' || !loggedIn.value || Date.now() < refreshDueAt) {
			return;
		}

		void ensureFreshAccessToken();
	}

	useEventListener(document, 'visibilitychange', refreshOnReturnToTab);

	// Registered from here, not in `layers/base`, which must not know the auth store.
	// Reports whether this refresh succeeded, not merely that a session still looks
	// live — a swallowed network failure would otherwise replay a doomed request.
	setUnauthorizedHandler(async () => !isEndingSession && loggedIn.value && await ensureFreshAccessToken());

	function loggedOut() {
		sessionRequests.invalidate();
		loggedIn.value = false;
		// A future session re-asks whether `/system/info` carries the authenticated
		// section, so don't let this session's spent one-shot answer for it.
		resetAuthProbe();
		currentUser.value = null;
		clearTimeout(refreshAccessTokenTimeout);

		// Drop any in-flight SSO context so a logout on a shared machine doesn't
		// leak the previous user's invite token / return-to URLs into the next
		// session in this tab.
		ssoReturnTo.clear();
		lastLoginMethod.setPending(undefined);
	}

	async function refreshAccessToken() {
		const request = sessionRequests.capture();

		try {
			// keepalive so a navigation can't abort the request mid-flight: the server
			// rotates the refresh token, so dropping the response loses the new cookie
			// and the next refresh gets a 401.
			const data = await refreshFetch<RefreshAccessTokenResult>('refresh', {
				method: 'POST',
				body: {},
				keepalive: true,
			});

			const result = RefreshAccessTokenResult.parse(data);

			if (!request.isCurrent()) {
				return false;
			}

			loggedIn.value = true;
			setAccessTokenExpiresIn(result.expires);

			return true;
		}
		catch (error) {
			if (getErrorStatusCode(error) === 401) {
				loggedOut();
				return false;
			}

			console.error('Error refreshing access token:', error);

			return false;
		}
	}

	async function fetchCurrentUser() {
		if (!loggedIn.value) {
			return;
		}

		const request = sessionRequests.capture();

		try {
			// Hydrate `authLinks.providerId` so `useAccountConnections` avoids a
			// duplicate `/me` round-trip; unwrap the BE's to-many data envelopes.
			type MeWire = Omit<AuthUser, 'authLinks' | 'workspaces'> & {
				authLinks?: { data: WireAuthLink[] };
				workspaces?: { data: AuthUser['workspaces'] };
			};
			const { data: raw } = await systemFetch<ResponseEnvelope<MeWire>>('system/users/me', {
				query: CURRENT_USER_FIELDS_QUERY,
			});
			const result: AuthUser = {
				...raw,
				authLinks: raw.authLinks?.data ?? [],
				workspaces: raw.workspaces?.data ?? [],
			};

			if (!request.isCurrent()) {
				return;
			}

			currentUser.value = result;
			// Resolved per call: the auth store boots before the system entity store exists (ADR 051).
			useSystemEntityStore().MonospaceUser.writeItem(result);
		}
		catch (error) {
			// 401 here means the refresh succeeded (logged-in flipped to true)
			// but the session can't read /me — treat as logged-out rather than
			// leaving the UI in a zombie "logged-in but no user" state.
			if (getErrorStatusCode(error) === 401) {
				loggedOut();
				return;
			}

			console.error('Error fetching current user:', error);
		}
	}

	function updateCurrentUser(user: MonospaceUser) {
		if (currentUser.value?.id !== user.id) return;

		currentUser.value = { ...currentUser.value, ...user };
	}

	const login = createAsyncAction(async (payload: { email: string; password: string }) => {
		try {
			const data = await authFetch<LoginResult>('providers/local/password/login', {
				method: 'POST',
				body: payload,
			});

			const result = LoginResult.parse(data);

			loggedIn.value = true;
			setAccessTokenExpiresIn(result.expires);

			// resolves terms acceptance without a separate `/system/info` probe.
			if (result.requireAcceptTerms) {
				overrideOnboardingResult({ requireAcceptTerms: result.requireAcceptTerms });
			}

			await fetchCurrentUser();

			lastLoginMethod.set(EMAIL_LOGIN_METHOD);
		}
		catch (error) {
			if (getErrorStatusCode(error) === 401) {
				throw new Error('$t:auth.login.error', { cause: error });
			}

			throw error;
		}
	});

	const bootstrap = createAsyncAction(async (payload: {
		fullName: string;
		email: string;
		password: string;
		acceptTerms: boolean;
		acceptMarketing: boolean;
	}) => {
		await systemFetch('system/bootstrap', {
			method: 'POST',
			body: payload,
		});

		// Re-probe before navigation so the next middleware run sees the
		// authoritative onboarding state, not stale "before-bootstrap" flags.
		await refreshSystemInfo();
	});

	const logout = createAsyncAction(async () => {
		if (!loggedIn.value) {
			return;
		}

		// Stop any further rotation, then let an in-flight refresh finish, so the
		// cookie this request presents is the one the engine still has a session for.
		isEndingSession = true;
		clearTimeout(refreshAccessTokenTimeout);
		await ensureFreshAccessToken.settled();

		try {
			await authFetch('logout', {
				method: 'POST',
				body: {},
			});
		}
		finally {
			loggedOut();
		}
	});

	async function requestPasswordReset(email: string) {
		await authFetch('providers/local/password/reset/request', {
			method: 'POST',
			body: { email },
		});
	}

	async function verifyPasswordResetToken(token: string): Promise<{ email: string }> {
		return await authFetch('providers/local/password/reset/verify', {
			method: 'GET',
			query: { token },
		});
	}

	async function resetPassword(token: string, password: string) {
		await authFetch('providers/local/password/reset/confirm', {
			method: 'POST',
			body: { token, password },
		});
	}

	async function changePassword(currentPassword: string, newPassword: string) {
		await authFetch('providers/local/password/change', {
			method: 'POST',
			body: { currentPassword, newPassword },
		});
	}

	// Side-effecting: POSTs then hands off to the IdP. Auth is the documented
	// exception for stores doing redirects, since login moves the user across
	// origins. Browser page-unload cancels the pending refreshAccessTokenTimeout
	// automatically. Hands off with `replace` so the login page leaves the back
	// stack — the SSO round-trip (login → IdP → callback → app) lands the user in
	// the app with no auth page reachable via Back.
	async function startSsoLogin(
		provider: Pick<WireAuthProviderListEntry, 'apiName' | 'loginPath'>,
		returnTo?: string,
	): Promise<void> {
		ssoReturnTo.setLoginReturnTo(returnTo);
		// Clear any stale signup marker so an abandoned signup attempt can't make
		// the shared `?auth=success` callback mistake this login for a signup.
		ssoReturnTo.setSignupContext(undefined);

		const result = await authFetch<{ redirectUrl: string }>(
			joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), provider.loginPath),
			{ method: 'POST', body: { returnTo: AUTH_ROUTES.callback } },
		);

		if (!result.redirectUrl) throw new Error('SSO login response is missing redirectUrl');

		// BE doesn't include the provider apiName in the success redirect, so
		// carry it via sessionStorage. callback.vue promotes to last-used only
		// on `authStatus === 'success'`. Set right before redirecting so a failed
		// POST above leaves no stale pending method behind.
		lastLoginMethod.setPending(provider.apiName);

		// No transition here: we're handing off to the IdP and the outcome isn't
		// known yet. The expand fires on confirmed success in `callback.vue`.
		redirect(result.redirectUrl, { replace: true });
	}

	// Side-effecting: POSTs then hands off to the IdP (see startSsoLogin).
	// `fullName` is required by the BE on `/oauth2/signup` since the IdP claim
	// may not carry it reliably across providers; FE collects it upfront.
	async function startSsoSignup(
		provider: Pick<WireAuthProviderListEntry, 'apiName' | 'signupPath'>,
		inviteToken: string,
		fullName: string,
	): Promise<void> {
		// Mark the signup flow so the shared `?auth=success` callback can tell it
		// apart from login; persist the invite token so a callback error can
		// route the user back to the invite page instead of generic login.
		ssoReturnTo.setLoginReturnTo(undefined);
		ssoReturnTo.setSignupContext({ inviteToken });

		lastLoginMethod.setPending(provider.apiName);

		try {
			const result = await authFetch<{ redirectUrl: string }>(
				joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), provider.signupPath),
				{ method: 'POST', body: { inviteToken, fullName, returnTo: AUTH_ROUTES.callback } },
			);

			if (!result.redirectUrl) throw new Error('SSO signup response is missing redirectUrl');

			// No transition here: handing off to the IdP, outcome not yet known.
			// The expand fires on confirmed success in `callback.vue`. `replace`
			// (like startSsoLogin): invite-accept is an auth page, so drop it from
			// the back stack; a callback error still offers "back to signup".
			redirect(result.redirectUrl, { replace: true });
		}
		catch (error) {
			// Undo the carry-across so a retry doesn't ship a stale provider through callback,
			// and drop the signup context so a failed start doesn't strand the invite token.
			lastLoginMethod.setPending(undefined);
			ssoReturnTo.setSignupContext(undefined);
			throw error;
		}
	}

	// Side-effecting: POSTs then redirects via window.location.href. Unlike the
	// SSO login/signup legs this starts from an in-app page (not an auth page),
	// so it keeps `href` — the originating page should stay in the back stack.
	async function startLinkFlow(
		provider: Pick<WireAuthProviderAdminRecord, 'apiName' | 'protocol'>,
		returnTo?: string,
	): Promise<void> {
		// Full path (query + hash) so a link started from the settings overlay
		// (`?settings=account/access#connections`) bounces back to the same
		// panel/anchor instead of dropping to the bare pathname.
		const target = returnTo ?? window.location.pathname + window.location.search + window.location.hash;
		ssoReturnTo.setLinkReturnTo(target);

		// Route through `/auth/callback` (same seam as login) so the FE can
		// translate `?link=error&code=X` into `?linkError=X` before bouncing
		// to the destination read from sessionStorage above.
		const result = await authFetch<{ redirectUrl: string }>(
			joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), 'link'),
			{ method: 'POST', body: { returnTo: AUTH_ROUTES.callback } },
		);

		if (!result.redirectUrl) throw new Error('SSO link response is missing redirectUrl');

		// Redirect straight away: the link flow navigates off to the provider, so waiting
		// on any mount/expand animation would only add a dead ~200ms delay.
		redirect(result.redirectUrl);
	}

	async function unlinkProvider(
		provider: Pick<WireAuthProviderAdminRecord, 'apiName' | 'protocol'>,
	): Promise<void> {
		await authFetch(
			joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), 'link'),
			{ method: 'DELETE' },
		);
	}

	let initialization: Promise<void> | undefined;

	function ensureInitialized() {
		return initialization ??= (async () => {
			await refreshAccessToken();
			await fetchCurrentUser();
		})();
	}

	return {
		currentUser,
		isOrganizationAdmin,
		login: login.execute,
		loginPending: login.pending,
		loginError: login.error,
		logout: logout.execute,
		logoutPending: logout.pending,
		logoutError: logout.error,
		bootstrap: bootstrap.execute,
		bootstrapPending: bootstrap.pending,
		bootstrapError: bootstrap.error,
		password: {
			requestReset: requestPasswordReset,
			verifyResetToken: verifyPasswordResetToken,
			reset: resetPassword,
			change: changePassword,
		},
		startSsoLogin,
		startSsoSignup,
		startLinkFlow,
		unlinkProvider,
		fetchCurrentUser,
		updateCurrentUser,
		ensureInitialized,
		loggedIn,
	};
});
